import type {
  ArticleListItem,
  DocListItem,
  DocType,
  Article,
  Publisher,
  Thread,
  ApiResultDocs,
  DocsUserDataResponse,
  DocUserDataWithContent,
  NoUserData,
} from './interfaces'
import { DocView, DocsView, DocSortOption, ContentView } from './doc'
import { backend } from '@/services/backend'
import type { RouteLocationRaw } from 'vue-router'

export class ArticleView extends DocView implements Article {
  public publisher: Publisher
  public thread: Thread
  public cover_thumbnail: string

  constructor(
    article: NoUserData<Article>,
    articleUserData: DocUserDataWithContent,
  ) {
    super(article, articleUserData)
    this.publisher = article.publisher
    this.thread = article.thread
    this.cover_thumbnail = article.cover_thumbnail
  }

  /**
   * Get a `DocListItem` version of this doc (ie. the base properties without `content`).
   */
  get asListItem(): ArticleListItem {
    return {
      ...super.asListItem,
      publisher: this.publisher,
      cover_thumbnail: this.cover_thumbnail,
    }
  }

  get page(): ContentView {
    return this._pages[0]
  }

  get nextLink(): { title: string; route: RouteLocationRaw } | null {
    if (!this.user?.next_url_slug || !this.user?.next_title) {
      return null
    }

    return {
      title: this.user.next_title,
      route: {
        name: this.routeNames().page,
        params: {
          url_slug: this.user.next_url_slug,
        },
      },
    }
  }

  get publisherLinkParams(): RouteLocationRaw {
    return {
      name: this.routeNames().listingPage,
      query: {
        search: this.publisher.name,
      },
    }
  }

  isCurrentThread(): boolean {
    return false
  }
}

export class ArticlesView extends DocsView {
  constructor(
    docs: DocListItem[],
    tags: string[],
    showCtaBlock: boolean = true,
    defaultSort: DocSortOption = DocSortOption.first_published_at,
  ) {
    // Sort option, 'created' is server-side default for articles
    super(docs, tags, defaultSort)
    this._showCtaBlock = showCtaBlock
  }

  get docs(): ArticleListItem[] {
    return super.docs as ArticleListItem[]
  }

  public get docTypeName(): DocType {
    return 'article'
  }

  static async createView(
    articlesResponse: ApiResultDocs,
    docsUserData?: DocsUserDataResponse,
    showCtaBlock: boolean = true,
    defaultSort: DocSortOption = DocSortOption.first_published_at,
  ): Promise<ArticlesView> {
    const view = new ArticlesView(
      articlesResponse.data,
      [],
      showCtaBlock,
      defaultSort,
    )
    if (docsUserData) {
      view.setUserData(docsUserData)
    }
    await view.init()
    return view
  }

  static async createSearchView(
    searchTerm: string = '',
    docsUserData: DocsUserDataResponse,
  ): Promise<ArticlesView> {
    const articles = searchTerm
      ? await DocsView.searchDocsLimited('article', searchTerm)
      : await backend.getArticles()

    // Search results already arrive ranked by the backend.
    const sortOption = searchTerm
      ? DocSortOption.none
      : DocSortOption.first_published_at
    return this.createView(articles, docsUserData, false, sortOption)
  }

  async _updateFilteredDocs(): Promise<void> {
    if (this.searchTerm !== null) {
      this.setSort(
        this.searchTerm ? DocSortOption.none : DocSortOption.first_published_at,
      )
    }

    // Override parent method to inject CTA block for free users.
    await super._updateFilteredDocs()

    if (this.needInjectCtaBlocks()) {
      /* For example, for the desktop screen width, there are 3 articles in a row:
       * | A | A | A |
       * | A | A | A |
       * We inject the first CTA block after 2 rows of articles (6 articles)
       * and the CTA block takes the space of two articles:
       * | A | A | A |
       * | A | A | A |
       * |  CTA  | A |
       * The gap between two CTA blocks is 10 articles (1 article + 3 rows)
       * We inject more CTA blocks after 3 more rows of articles:
       * | A | A | A |
       * | A | A | A |
       * |  CTA  | A |
       * | ... 3 rows of articles ... |
       * |  CTA  | A |
       *
       * The same CTA blocks positions are preserved on tablet/mobile screen
       * widths, meaning that CTA block is still on 7, 18, 29, ... positions.
       */
      super.injectCtaBlocks(6, 10)
    }
  }

  /**
   *  Url for cover image to show on list page.
   */
  getItemCoverUrl(article: ArticleListItem): string {
    return article.cover_thumbnail
  }
}
